// Device classification — phone / tablet / desktop, touch, native shell.
//
// One source of truth for "what are we running on", shared by the shell
// (PhoneShell vs the desktop sidebar layout) and the primitives that change
// presentation on touch (Drawer → sheet, Combobox → bottom sheet, Btn sizes).
//
// Rules (keep in sync with the bootstrap script in index.html, which sets the
// same <html> classes before first paint so there's no layout flash):
//   • Only a touch device (coarse pointer) or the native shell ever leaves the
//     desktop layout — a narrow desktop browser window stays desktop, which is
//     the zero-regression guarantee for laptops.
//   • phone  = touch && the SHORT side < 600px. Using the short side keeps an
//     iPhone in landscape (up to ~956px wide) on the phone layout.
//   • tablet = touch && not phone (iPad in either orientation, Split View).
//   • compact = tablet && width < 1024 — iPad portrait / Split View, where the
//     220px sidebar becomes an overlay instead of eating a third of the width.
//   • Override for testing in a desktop browser: ?shell=phone|tablet|desktop
//     (persisted to localStorage 'atlas.shell'; ?shell=auto clears it).
//
// Exposes window.AtlasDevice (live snapshot), window.useDevice() (re-renders
// on change) and fires 'atlas-device' on window when the class changes.

(function () {
  const OVERRIDE_KEY = 'atlas.shell';
  const mq = (q) => { try { return window.matchMedia(q).matches; } catch (_) { return false; } };

  const readOverride = () => {
    try {
      const m = /[?&]shell=(phone|tablet|desktop|auto)\b/.exec(window.location.search || '');
      if (m) {
        if (m[1] === 'auto') localStorage.removeItem(OVERRIDE_KEY);
        else localStorage.setItem(OVERRIDE_KEY, m[1]);
      }
      const v = localStorage.getItem(OVERRIDE_KEY);
      return v === 'phone' || v === 'tablet' || v === 'desktop' ? v : null;
    } catch (_) { return null; }
  };

  const isNative = () => {
    try { return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()); }
    catch (_) { return false; }
  };

  const classify = () => {
    const w = window.innerWidth || 1280;
    const h = window.innerHeight || 800;
    const native = isNative();
    const coarse = mq('(pointer: coarse)');
    const touch = native || coarse;
    const canHover = mq('(hover: hover)') && !coarse;
    const override = readOverride();
    let kind;
    if (override) kind = override;
    else if (touch && Math.min(w, h) < 600) kind = 'phone';
    else if (touch) kind = 'tablet';
    else kind = 'desktop';
    const phone = kind === 'phone';
    const tablet = kind === 'tablet';
    return {
      kind, phone, tablet, desktop: kind === 'desktop',
      // An override to phone/tablet implies touch-style UI even on a mouse.
      touch: touch || phone || tablet,
      canHover: canHover && !phone,
      native,
      platform: native && window.Capacitor.getPlatform ? window.Capacitor.getPlatform() : 'web',
      standalone: mq('(display-mode: standalone)') || window.navigator.standalone === true,
      compact: tablet && w < 1024,
      landscape: w > h,
      width: w, height: h,
    };
  };

  const applyClasses = (d) => {
    const el = document.documentElement;
    el.classList.toggle('is-phone', d.phone);
    el.classList.toggle('is-tablet', d.tablet);
    el.classList.toggle('is-desktop', d.desktop);
    el.classList.toggle('is-touch', d.touch);
    el.classList.toggle('is-native', d.native);
    el.classList.toggle('is-compact', d.compact);
    el.dataset.device = d.kind;
  };

  let current = classify();
  applyClasses(current);
  window.AtlasDevice = current;

  // Re-classify on resize/rotation. Only a change in the fields the UI keys
  // off (kind, compact, landscape) notifies subscribers — a keyboard opening
  // shrinks innerHeight on Android and must not churn every subscriber.
  const listeners = new Set();
  let raf = 0;
  const recompute = () => {
    raf = 0;
    const next = classify();
    const changed = next.kind !== current.kind || next.compact !== current.compact
      || next.landscape !== current.landscape || next.native !== current.native;
    current = next;
    window.AtlasDevice = next;
    if (!changed) return;
    applyClasses(next);
    listeners.forEach((fn) => { try { fn(next); } catch (_) {} });
    try { window.dispatchEvent(new CustomEvent('atlas-device', { detail: next })); } catch (_) {}
  };
  const schedule = () => { if (!raf) raf = requestAnimationFrame(recompute); };
  window.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);

  window.subscribeDevice = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

  // React hook — returns the live snapshot and re-renders when it changes.
  window.useDevice = () => {
    const [d, setD] = React.useState(() => window.AtlasDevice);
    React.useEffect(() => window.subscribeDevice(setD), []);
    return d;
  };

  // On-screen keyboard. iOS Safari doesn't shrink the layout viewport when the
  // keyboard opens — only window.visualViewport does — so a full-screen sheet
  // sized to 100% keeps its Save button under the keyboard. Publish the
  // visible height as --vvh (and the keyboard's height as --kb) for sheets to
  // size against. The native shell resizes the webview itself, so --kb stays 0.
  if (window.visualViewport) {
    const vv = window.visualViewport;
    const onVV = () => {
      const root = document.documentElement;
      root.style.setProperty('--vvh', Math.round(vv.height) + 'px');
      root.style.setProperty('--kb', Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop)) + 'px');
    };
    vv.addEventListener('resize', onVV);
    vv.addEventListener('scroll', onVV);
    onVV();
  }

  // Keep the browser chrome (theme-color: Safari's toolbar tint, the Home
  // Screen app's status bar) in step with Atlas's own light/dark theme, which
  // is user-chosen rather than the system one.
  // Desktop keeps the static white theme-color it always had.
  const syncChrome = () => {
    const dark = document.documentElement.dataset.theme === 'dark';
    if (!current.desktop) {
      // Hex, not the --surface oklch() token — older WebKit ignores oklch in
      // theme-color. #1b1c22 ≈ the dark theme's --surface.
      try {
        document.querySelectorAll('meta[name="theme-color"]').forEach((m) => m.setAttribute('content', dark ? '#1b1c22' : '#ffffff'));
      } catch (_) {}
    }
    // The native status bar follows the theme in src/native.jsx.
  };
  try {
    new MutationObserver(syncChrome).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  } catch (_) {}
  syncChrome();

  // Hover styling helper for inline onMouseEnter/Leave handlers: on touch
  // devices iOS fires a synthetic mouseenter on tap and never the leave, which
  // leaves rows/buttons stuck in their hover color. Wrap the handler instead:
  //   onMouseEnter={hoverOnly((e) => { ... })}
  window.hoverOnly = (fn) => (e) => { if (!window.AtlasDevice || window.AtlasDevice.canHover !== false) fn(e); };
})();

// Pick the layout by hand (More → "Desktop layout" on a phone, avatar menu →
// "Phone layout" on an iPad). null = automatic. Reloads so every view mounts
// fresh in the new shell.
window.setShellOverride = (kind) => {
  try {
    if (kind) localStorage.setItem('atlas.shell', kind);
    else localStorage.removeItem('atlas.shell');
  } catch (_) {}
  window.location.reload();
};
window.getShellOverride = () => { try { return localStorage.getItem('atlas.shell'); } catch (_) { return null; } };
